import { createFeatureSelector, createSelector } from '@ngrx/store'
import { Message, MessageType } from '@tv3/store/message/message.model'
import { MessageView } from '@tv3/store/message/message.view'
import * as reducer from '@tv3/store/message/message.reducer'
import * as state from '@tv3/store/message/message.state'
import { selectInquiries } from '@tv3/store/inquiry/inquiry.selectors'
import { selectUserPreferences } from '@tv3/store/user-preferences/user-preferences.selectors'
import { selectAllRentals } from '@tokeet-frontend/tv3-platform'
import * as lodash from 'lodash'

export const messageState = createFeatureSelector<state.State>('message')

export const selectMessagesLoaded = createSelector(messageState, (state) => state.isLoaded)
export const selectMessagesLoading = createSelector(messageState, (state) => state.isLoading)
export const isAddingMessage = createSelector(messageState, (state) => state.isAddingMessage)
export const selectUnreadCount = createSelector(messageState, (state) => state.unreadCount)
export const selectUnreadCountLoaded = createSelector(messageState, (state) => state.isUnreadCountLoaded)
export const selectUnreadCountLoading = createSelector(messageState, (state) => state.isUnreadCountLoading)

export const selectAllMessages = createSelector(messageState, reducer.selectAll)

export const selectMessageSources = createSelector(selectAllMessages, (messages: Message[]) => {
  return lodash
    .uniq(messages.map((m) => lodash.toLower(m.sentWith)))
    .filter((s) => s)
    .map((s) => ({ id: s, label: lodash.capitalize(s) }))
})

export const selectUnreadMessages = createSelector(selectAllMessages, (messages: Message[]) => {
  return (messages || [])
    .filter((m) => m.type === MessageType.Inbound || m.type === MessageType.Guest)
    .filter((m) => !m.read)
})

export const selectMessagesByIds = (messageIds: string[]) =>
  createSelector(selectAllMessages, (messages: Message[]) => {
    return messages.filter((m) => messageIds.includes(m.id))
  })

export const selectMessagesByGuestId = (guestId: string) =>
  createSelector(selectAllMessages, (messages: Message[]) => {
    return (messages || []).filter((m) => m.guestId === guestId)
  })

export const selectMessagesByInquiryId = (inquiryId: string, linkedIds: string[] = []) =>
  createSelector(selectAllMessages, (messages: Message[]) => {
    return (messages || []).filter((m) => m.inquiryId === inquiryId || linkedIds?.includes(m.inquiryId))
  })

export const selectAllInboundMessageViews = createSelector(
  selectAllMessages,
  selectInquiries,
  selectAllRentals,
  selectUserPreferences,
  (messages, inquiries, rentals, userPreferences) => {
    const rentalsById = lodash.keyBy(rentals, 'id')
    const inquiresById = lodash.keyBy(inquiries, 'id')
    return messages
      .filter((m) => m.type === MessageType.Inbound || m.type === MessageType.Guest)
      .map((m) => {
        const inquiry = inquiresById[m.inquiryId] || ({} as any)
        const rental = rentalsById[m.rentalId || inquiry.rentalId]
        return {
          ...m,
          guestName: lodash.trim(m.guestName || m.name) || $localize`Unknown`,
          rentalName: (rental && rental.name) || '',
          pinned: (userPreferences.pinnedMessages || []).indexOf(m.id) > -1,
          inquiry,
          rental,
        } as MessageView
      })
  }
)

export const selectPinnedMessageViews = createSelector(selectAllInboundMessageViews, (messages) =>
  messages.filter((m) => m.pinned)
)
